"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Truck, ShieldCheck, ArrowRight, Droplets, Flame, Bug, Biohazard, Building2, Hammer, FileCheck, PhoneCall, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const marylandCounties = [{
  name: "Montgomery",
  desc: "Bethesda, Silver Spring, Rockville, Gaithersburg, and Germantown corridors.",
  badge: "24/7 Mobile Unit",
  to: "/MontgomeryCountyMDRestorationServices"
}, {
  name: "Prince George's",
  desc: "Bowie, Laurel, College Park, Hyattsville, and surrounding communities.",
  badge: "Direct Dispatch",
  to: "/PrinceGeorgesCountyMDRestorationServices"
}, {
  name: "Frederick",
  desc: "Frederick City, Urbana, Walkersville, and central county routes.",
  badge: "Rapid Response",
  to: "/FrederickCountyMdRestorationServices"
}, {
  name: "Anne Arundel",
  desc: "Annapolis, Glen Burnie, Severna Park, and waterfront properties.",
  badge: "On-Site Crews",
  to: "/AnneArundelCountyMdRestorationServices"
}, {
  name: "Baltimore",
  desc: "Towson, Catonsville, Owings Mills, Dundalk, and county lines.",
  badge: "Emergency Fleet",
  to: "/BaltimoreCountyMdRestorationServices"
}, {
  name: "Howard",
  desc: "Columbia, Ellicott City, Clarksville, and Route 29/I-95 points.",
  badge: "Direct Dispatch",
  to: "/HowardCountyMDRestorationServices"
}];
const restorationServices = [{
  title: "Water Damage & Flood Restoration",
  to: "/WaterDamageFloodRestorationPage",
  icon: Droplets,
  detail: "Rapid structural extraction, industrial dehumidification, and moisture tracing."
}, {
  title: "Mold Remediation",
  to: "/MoldRemediationPage",
  icon: Bug,
  detail: "Containment barriers, negative air filtration, and safe microbial removal."
}, {
  title: "Fire & Smoke Odor Restoration",
  to: "/FireSmokeOdorRestorationPage",
  icon: Flame,
  detail: "Soot neutralization, thermal fogging, and property stabilization."
}, {
  title: "Sewage Cleanup",
  to: "/SewageCleanupPage",
  icon: Droplets,
  detail: "Category 3 sanitation, antimicrobial treatment, and safe extraction."
}, {
  title: "Biohazard Cleanup",
  to: "/BiohazardCleanupPage",
  icon: Biohazard,
  detail: "OSHA-compliant decontamination, biological removal, and disposal."
}, {
  title: "Asbestos & Lead Abatement",
  to: "/AsbestosLeadAbatementPage",
  icon: FileCheck,
  detail: "Certified testing, hazardous material abatement, and air quality clearance."
}, {
  title: "Reconstruction & Design-Build",
  to: "/ReconstructionDesignBuildPage",
  icon: Hammer,
  detail: "Full structural rebuilding, drywall repairs, and finish restoration."
}, {
  title: "Commercial & HOA Property Restoration",
  to: "/CommercialHOAPropertyRestorationPage",
  icon: Building2,
  detail: "Large-scale portfolio response, mitigation plans, and board coordination."
}];
export default function MarylandArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="marylandservicearea" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-primary border-primary/30">
              Regional Coverage
            </Badge>
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Direct Field Deployment
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Maryland service areas and mobile dispatch
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            TriServe dispatches mobile restoration crews directly to homes and commercial facilities across central and western Maryland. We coordinate repairs and insurance mitigation on-site with local teams, operating without a public walk-in office in the state.
          </p>
        </div>

        {/* Dispatch Notification Callout */}
        <div className="mb-14 rounded-xl border border-border bg-muted/40 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary/10 text-primary p-3 shrink-0">
              <Truck className="size-6" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-base sm:text-lg">
                Mobile-first operational model
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                TriServe does not maintain a physical branch office or walk-in showroom in Maryland. Our technicians, drying trailers, and mitigation equipment are stationed regionally for direct 24/7 arrival at your property.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild variant="default" className="w-full md:w-auto">
              <Link to="/ContactRequestServicePage">
                <PhoneCall className="mr-2 size-4" />
                Request service
              </Link>
            </Button>
          </div>
        </div>

        {/* Maryland Counties Grid */}
        <div className="mb-16 lg:mb-20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
                Counties served
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Active daily response routes throughout these designated county jurisdictions.
              </p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {marylandCounties.map((county, index) => {
            const cardMarkup = <Card className="h-full border border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-xs">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="size-4 text-primary" />
                        <CardTitle className="text-lg font-medium">
                          {county.name} County
                        </CardTitle>
                      </div>
                      <Badge variant="secondary" className="text-2xs font-normal">
                        {county.badge}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {county.desc}
                    </CardDescription>
                  </CardContent>
                </Card>;
            return <motion.div key={county.name} variants={itemVariants} data-index={index}>
                  {county.to ? <Link to={county.to} className="block h-full">
                      {cardMarkup}
                    </Link> : cardMarkup}
                </motion.div>;
          })}
          </motion.div>
        </div>

        <Separator className="my-12 lg:my-16" />

        {/* Restoration Services Offered in Maryland */}
        <div>
          <div className="max-w-2xl mb-8">
            <div className="flex items-center gap-2 text-primary font-medium text-sm mb-2">
              <ShieldCheck className="size-4" />
              <span>Full-Scope Capabilities</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
              Restoration services deployed in Maryland
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              Every specialized service is handled directly by certified field crews equipped for commercial and residential mitigation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {restorationServices.map((service, index) => {
            const IconComp = service.icon;
            return <div key={service.title} data-index={index}>
                  <Link to={service.to} className="group block h-full p-5 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-muted/30 transition-[color,background-color,border-color,box-shadow] duration-200 shadow-2xs">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComp className="size-4" />
                      </div>
                      <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-transform duration-200" />
                    </div>
                    <h4 className="font-semibold text-sm text-foreground mb-1.5 line-clamp-2">
                      {service.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-normal line-clamp-3">
                      {service.detail}
                    </p>
                  </Link>
                </div>;
          })}
          </div>

          {/* Quick Contact & Dispatch Guarantee */}
          <div className="mt-12 rounded-xl bg-card border border-border p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="size-2 rounded-full bg-primary animate-pulse shrink-0" />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="size-4 text-foreground shrink-0" />
                <span>24/7 direct crew dispatch. No third-party call centers.</span>
              </div>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/ContactRequestServicePage">
                Connect with local technicians
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}